import { useCallback, useMemo } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import { Permission, PermissionEntitiesNames } from '@fabric-ui/components';
import { EntityProjectionName, entityProjectionNames } from '@utils/entityProjectionNames';

/**
 * Configuration for Risk Score permissions per entity projection.
 * Defines which permission resource is required for each projection.
 */

type PermissionsMap = Record<
  EntityProjectionName,
  {
    permissionResource: PermissionEntitiesNames;
    editableBaseScores: string[];
  }
>;

export const RISK_SCORE_PERMISSION_CONFIG: Partial<PermissionsMap> = {
  [entityProjectionNames.Asset]: {
    permissionResource: PermissionEntitiesNames.ASSET_RISK_SCORE,
    // Asset Risk Score: Update can edit Asset business context and Policy violation
    editableBaseScores: [entityProjectionNames.Asset, entityProjectionNames.PolicyViolation],
  },
  [entityProjectionNames.Finding]: {
    permissionResource: PermissionEntitiesNames.FINDING_RISK_SCORE,
    // Finding Risk Score: Update can edit Finding base score and Asset business context
    editableBaseScores: [entityProjectionNames.Finding, entityProjectionNames.Asset],
  },
};

export interface RiskScorePermissions {
  // Generic permissions for current projection
  canRead: boolean;
  canUpdate: boolean;
  canEditCompoundWeights: boolean;
  canEditBaseScores: boolean;

  // Helper functions
  canEditBaseScore: (baseScoreProjectionName: EntityProjectionName) => boolean;
  isReadOnly: boolean;

  // Raw permission checks (for debugging/advanced use)
  hasPermission: (projection: EntityProjectionName, permission: Permission) => boolean;
}

/**
 * Hook to check Risk Score permissions based on the requirements document.
 *
 * Permission Rules:
 * - Read permission: View all, edit nothing
 * - Update permission: Edit compound weights and allow-listed base scores
 *
 * Base score editing rules:
 * - A base score is editable if the user has UPDATE on the current projection AND
 *   the base score appears in that projection's editableBaseScores allow-list.
 * - Dependency-based editing (e.g. Finding UPDATE → Asset base score) is handled by
 *   including the dependency in the projection's editableBaseScores, not by cross-permission checks.
 *
 * Configuration:
 * - RISK_SCORE_PERMISSION_CONFIG: Maps each projection to its permission resource and editable base scores
 */
export const useRiskScorePermissions = (riskScoreProjectionName: EntityProjectionName): RiskScorePermissions => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();

  const config = RISK_SCORE_PERMISSION_CONFIG[riskScoreProjectionName];

  /**
   * Generic permission checker for any projection
   */
  const hasPermission = useCallback(
    (projection: EntityProjectionName, permission: Permission) => {
      const projectionConfig = RISK_SCORE_PERMISSION_CONFIG[projection];
      if (!projectionConfig) return false;

      return hasAllowedPermissionToResource({
        resource: projectionConfig.permissionResource,
        permission,
      });
    },
    [hasAllowedPermissionToResource]
  );

  if (!config) {
    console.warn(`No permission configuration found for projection: ${riskScoreProjectionName}`);
    return {
      canRead: false,
      canUpdate: false,
      canEditCompoundWeights: false,
      canEditBaseScores: false,
      canEditBaseScore: () => false,
      isReadOnly: true,
      hasPermission,
    };
  }

  // Check permissions for current projection
  const canRead = useMemo(
    () => config && (hasPermission(riskScoreProjectionName, Permission.READ) || hasPermission(riskScoreProjectionName, Permission.UPDATE)),
    [config, hasPermission, riskScoreProjectionName]
  );

  const canUpdate = useMemo(
    () => config && hasPermission(riskScoreProjectionName, Permission.UPDATE),
    [config, hasPermission, riskScoreProjectionName]
  );

  /**
   * Determines if a specific base score can be edited.
   *
   * A base score is editable if:
   * 1. The user has UPDATE permission on the current risk score projection, AND
   * 2. The base score is in the current projection's allow-list (editableBaseScores).
   *
   * This covers both direct ownership (e.g. Asset UPDATE → Asset base score)
   * and dependency-based editing (e.g. Finding UPDATE → Asset base score,
   * because Asset is listed in Finding's editableBaseScores).
   */
  const canEditBaseScore = useCallback(
    (baseScoreProjectionName: EntityProjectionName) => {
      if (!config || !canUpdate) return false;
      return config.editableBaseScores.includes(baseScoreProjectionName) || hasPermission(baseScoreProjectionName, Permission.UPDATE);
    },
    [config, canUpdate]
  );

  const isReadOnly = !canUpdate;

  const canEditCompoundWeights = canUpdate;

  const canEditBaseScores = canUpdate;

  return {
    canRead,
    canUpdate,
    canEditCompoundWeights,
    canEditBaseScores,
    canEditBaseScore,
    isReadOnly,
    hasPermission,
  };
};
